import { useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';

import { RecordingStartConfirmDialog } from '@/components/molecules/voice-recording';
import { MeDocument } from '@/generated/usermobile-bff/graphql';
import { useAiTranscriptionConsentDialog } from '@/hooks';
import { useTranslation } from '@/i18n';
import { useVoiceRecordingStore } from '@/stores';
import { unisonColors } from '@/theme';

interface MeetingRecordingPromptProps {
  readonly meetingId: string;
}

export const MeetingRecordingPrompt = ({ meetingId }: MeetingRecordingPromptProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { data: meData } = useQuery(MeDocument);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const { handleAccept, consentDialog, isConsentVisible } = useAiTranscriptionConsentDialog();
  const recordingStatus = useVoiceRecordingStore((state) => state.recording.status);
  const requestMicrophonePermission = useVoiceRecordingStore(
    (state) => state.requestMicrophonePermission,
  );

  const isRecordingUnavailable = recordingStatus !== 'idle';

  const handleRequestStartRecording = useCallback(() => {
    if (isRecordingUnavailable) {
      return;
    }

    handleAccept(() => {
      setShowStartDialog(true);
    });
  }, [handleAccept, isRecordingUnavailable]);

  const handleConfirmStartRecording = useCallback(async () => {
    const me = meData?.me;
    if (!me?.backendAppUserId || me.accountId == null) {
      return;
    }

    setShowStartDialog(false);

    const granted = await requestMicrophonePermission();
    if (!granted) {
      return;
    }

    router.push({
      pathname: '/recording',
      params: {
        meetingId,
        userId: String(me.backendAppUserId),
        accountId: String(me.accountId),
      },
    });
  }, [meData?.me, meetingId, requestMicrophonePermission, router]);

  const handleDismissStartRecording = useCallback(() => {
    setShowStartDialog(false);
  }, []);

  return (
    <>
      <Pressable
        accessibilityRole="link"
        disabled={isRecordingUnavailable}
        onPress={handleRequestStartRecording}
        style={({ pressed }) => [pressed && !isRecordingUnavailable ? styles.pressed : null]}
        testID="meeting-start-recording-link">
        <Text style={styles.linkText}>{t('meetings.detail.scheduledRecordingCta')}</Text>
      </Pressable>
      <RecordingStartConfirmDialog
        visible={showStartDialog && !isConsentVisible}
        consentDialog={consentDialog}
        onConfirm={handleConfirmStartRecording}
        onDismiss={handleDismissStartRecording}
      />
    </>
  );
};

const styles = StyleSheet.create({
  linkText: {
    color: unisonColors.onInfoContainer,
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
  },
  pressed: {
    opacity: 0.6,
  },
});
